// =============
// STRING FORMAT
// =============

export function capitalize(str: string) {
    if (!str) return ''
    // Regular expression to match every consecutive alphabetic chars or number
    const alphabeticChar = /[0-9A-Z\u00C0-\u00DC]+/gi

    return str.replace(
        alphabeticChar,
        // For every consecutive list of alphabetic chars, make the first to uppercase and return the rest
        (word) => word.charAt(0).toUpperCase() + word.substr(1).toLowerCase()
    )
}

export const capitalizeAll = (text?: string) => `${text}`.split(' ').map(capitalize).join(' ')

/**
 * Removes a common left indentation from a multiline Markdown string.
 *
 * Behavior:
 * - Converts tabs to spaces (4 spaces per tab).
 * - Trims leading and trailing blank lines to avoid false-zero indentation.
 * - Computes the minimal positive indentation across non-empty lines and removes it.
 *
 * This prevents Markdown content (e.g., tables) from being interpreted as code blocks
 * due to accidental indentation when embedding template literals.
 *
 * @param src Original Markdown string that may contain leading indentation
 * @returns The Markdown string with common indentation removed
 */
export const normalizeIndent = (src: string) => {
    // Normalize tabs -> spaces and trim leading/trailing blank lines
    const trimmed = src.replace(/\t/g, '    ').replace(/^\n+|\n+$/g, '')
    const lines = trimmed.split('\n')
    // Consider only lines with positive indentation to compute the minimum
    const indents = lines
        .filter((l) => l.trim().length > 0)
        .map((l) => l.match(/^ +/u)?.[0].length ?? 0)
        .filter((n) => n > 0)
    if (indents.length === 0) return trimmed
    const min = Math.min(...indents)
    const prefix = ' '.repeat(min)
    return lines.map((l) => (l.startsWith(prefix) ? l.slice(min) : l)).join('\n')
}
